/**
 * Phone tab — show and update WhatsApp / mobile number (UserV2 no_hp).
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, MessageCircle } from "lucide-react";
import type { User } from "@/services/authService";
import { authService } from "@/services/authService";
import { userService } from "@/services/userService";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface PhoneTabProps {
  user: User;
  /** If false, read-only (e.g. viewing another user). */
  allowEdit?: boolean;
  /** After a successful save when the viewed profile may differ from auth user (e.g. admin). */
  onPhoneSaved?: () => void | Promise<void>;
}

function formatPhoneForWhatsApp(phone: string): string {
  let cleaned = phone.replace(/\D/g, "");
  if (cleaned.startsWith("0")) {
    cleaned = "62" + cleaned.substring(1);
  } else if (!cleaned.startsWith("62")) {
    cleaned = "62" + cleaned;
  }
  return cleaned;
}

export function PhoneTab({ user, allowEdit = true, onPhoneSaved }: PhoneTabProps) {
  const { refreshUser, user: authUser } = useAuth();
  const [isEditing, setIsEditing] = React.useState(false);
  const [noHp, setNoHp] = React.useState(user.no_hp || "");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!isEditing) {
      setNoHp(user.no_hp || "");
    }
  }, [user.no_hp, isEditing]);

  const whatsappUrl = user.no_hp
    ? `https://wa.me/${formatPhoneForWhatsApp(user.no_hp)}`
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const trimmed = noHp.trim();
    if (trimmed) {
      const digits = trimmed.replace(/\D/g, "");
      if (digits.length < 8 || digits.length > 15) {
        setError("Nomor HP harus berisi 8–15 digit");
        return;
      }
    }

    setIsLoading(true);
    try {
      const isSelf = authUser?.id === user.id;
      if (isSelf) {
        await authService.updateProfile({ no_hp: trimmed });
      } else {
        await userService.updateUserPhone(user.id, trimmed);
      }
      toast.success("Nomor HP berhasil diperbarui");
      await refreshUser();
      await onPhoneSaved?.();
      setIsEditing(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Gagal memperbarui nomor HP";
      setError(errorMessage);
      toast.error("Gagal memperbarui nomor HP", { description: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setNoHp(user.no_hp || "");
    setError("");
    setIsEditing(false);
  };

  const unchanged = noHp.trim() === (user.no_hp || "").trim();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Nomor HP
          </CardTitle>
          <CardDescription>
            Nomor digunakan untuk kontak dan layanan terkait keanggotaan. Pastikan nomor aktif (mis. WhatsApp).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!allowEdit ? (
            <div className="space-y-2">
              <Label className="text-muted-foreground">Nomor saat ini</Label>
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm">
                <span>{user.no_hp?.trim() || "—"}</span>
                {whatsappUrl ? (
                  <Button variant="outline" size="sm" className="gap-1" asChild>
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="h-4 w-4" />
                      WhatsApp
                    </a>
                  </Button>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                Hanya pemilik akun atau super admin yang dapat mengubah nomor ini.
              </p>
            </div>
          ) : !isEditing ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Nomor saat ini</Label>
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm">
                  <span>{user.no_hp?.trim() || "—"}</span>
                  {whatsappUrl ? (
                    <Button variant="outline" size="sm" className="gap-1" asChild>
                      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="h-4 w-4" />
                        WhatsApp
                      </a>
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="button" variant="secondary" onClick={() => setIsEditing(true)}>
                  Ubah nomor
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="no_hp">Nomor HP</Label>
                <Input
                  id="no_hp"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="Contoh: 081234567890"
                  value={noHp}
                  onChange={(e) => setNoHp(e.target.value)}
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Kosongkan lalu simpan untuk menghapus nomor (tidak disarankan).
                </p>
              </div>
              {error ? <p className="text-sm text-destructive">{error}</p> : null}
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Batal
                </Button>
                <Button type="submit" disabled={isLoading || unchanged}>
                  {isLoading ? "Menyimpan…" : "Simpan"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
